import { useState, useEffect, useRef, useId } from 'react';
import { Button } from '../../ui/button';
import { formatCurrency } from '../HiveFundInvestFlow.utils';

interface AmountStepProps {
  investmentAmount: number;
  onAmountChange: (amount: number) => void;
  onContinue: () => void;
  canContinue: boolean;
  minInvestment: number;
  presetAmounts: number[];
}

export function AmountStep({
  investmentAmount,
  onAmountChange,
  onContinue,
  canContinue,
  minInvestment,
  presetAmounts,
}: AmountStepProps) {
  const headingRef = useRef<HTMLHeadingElement>(null);
  const inputId = useId();
  const [rawInput, setRawInput] = useState(investmentAmount > 0 ? String(investmentAmount) : '');

  useEffect(() => {
    headingRef.current?.focus();
  }, []);

  const handleInputChange = (value: string) => {
    setRawInput(value);
    const parsed = parseInt(value, 10);
    onAmountChange(Number.isNaN(parsed) ? 0 : parsed);
  };

  const handlePresetClick = (amount: number) => {
    setRawInput(String(amount));
    onAmountChange(amount);
  };

  const estimatedQuarterlyLow = investmentAmount >= minInvestment ? (investmentAmount * 0.08) / 4 : 0;
  const estimatedQuarterlyHigh = investmentAmount >= minInvestment ? (investmentAmount * 0.12) / 4 : 0;

  return (
    <div className="space-y-8">
      <div className="text-center">
        <h1
          ref={headingRef}
          tabIndex={-1}
          className="text-3xl md:text-4xl mb-2 focus-visible:outline-none"
        >
          How much would you like to invest?
        </h1>
        <p className="text-lg text-muted-foreground">
          Minimum investment: {formatCurrency(minInvestment)}
        </p>
      </div>

      {/* Preset amounts */}
      <div className="grid grid-cols-2 gap-3" role="radiogroup" aria-label="Preset investment amounts">
        {presetAmounts.map((amount) => (
          <button
            key={amount}
            role="radio"
            aria-checked={investmentAmount === amount}
            onClick={() => handlePresetClick(amount)}
            className={`py-4 rounded-xl font-medium text-lg transition border-2 ${
              investmentAmount === amount
                ? 'border-primary bg-primary/5 text-foreground'
                : 'border-border bg-card text-foreground hover:border-primary/50'
            }`}
          >
            ${amount.toLocaleString()}
          </button>
        ))}
      </div>

      {/* Custom amount */}
      <div>
        <label htmlFor={inputId} className="block text-sm font-medium text-muted-foreground mb-2">
          Or enter a custom amount
        </label>
        <div className="relative">
          <span className="absolute left-4 top-1/2 -translate-y-1/2 text-muted-foreground text-lg" aria-hidden="true">
            $
          </span>
          <input
            id={inputId}
            type="number"
            min={minInvestment}
            step="100"
            value={rawInput}
            onChange={(e) => handleInputChange(e.target.value)}
            placeholder="Enter amount"
            className="w-full pl-10 pr-4 py-4 bg-background border-2 border-border rounded-xl text-foreground text-lg placeholder-muted-foreground focus:border-primary focus:outline-none focus:ring-4 focus:ring-primary/20 transition"
            aria-describedby={investmentAmount > 0 && investmentAmount < minInvestment ? `${inputId}-error` : undefined}
          />
        </div>
        {investmentAmount > 0 && investmentAmount < minInvestment && (
          <p id={`${inputId}-error`} className="mt-2 text-sm text-muted-foreground" role="alert">
            Minimum investment is {formatCurrency(minInvestment)}
          </p>
        )}
      </div>

      {/* Estimated returns */}
      {investmentAmount >= minInvestment && (
        <div className="rounded-xl border-2 border-border bg-card p-6" aria-live="polite">
          <h3 className="text-lg font-medium mb-4">Estimated Returns</h3>
          <div className="space-y-3">
            <div className="flex justify-between">
              <span className="text-muted-foreground">Annual Return</span>
              <span className="font-medium">8–12%</span>
            </div>
            <div className="flex justify-between">
              <span className="text-muted-foreground">Quarterly Distribution</span>
              <span className="font-medium">
                {formatCurrency(estimatedQuarterlyLow)} – {formatCurrency(estimatedQuarterlyHigh)}
              </span>
            </div>
          </div>
        </div>
      )}

      <Button
        className="w-full h-14 text-lg disabled:opacity-40"
        onClick={onContinue}
        disabled={!canContinue}
      >
        Continue
      </Button>
    </div>
  );
}
